import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import s from './ListRow.module.css'

export type ListRowSize = 'sm' | 'md'

export interface ListRowProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** The leading mark — an `IconTile`, a selection mark. Decorative: the
      row's accessible name is its label, so the slot is `aria-hidden`. */
  leading?: ReactNode
  /** The primary line. Truncates to one line. */
  label: ReactNode
  /** The secondary line under it, muted, truncating to one line. */
  detail?: ReactNode
  /** What the row carries at its end — a count, a type pill, a timestamp, a
      state word. Bare text here reads as metadata (xs, muted); a kit control
      placed here keeps its own paint. */
  trailing?: ReactNode
  /** The chosen row of a set. Drawn by the row, carried to assistive
      technology by the host's own `aria-selected` / `aria-current`. */
  selected?: boolean
  /** `sm` for a drawer list, `md` (default) for a dialog or a pane. */
  size?: ListRowSize
}

/**
 * ONE ROW OF A LIST — a mark, a primary line over a truncating secondary, and
 * whatever the row carries at its end (#2406).
 *
 * ── why this exists, which is not "it seemed reusable" ─────────────────────
 * Three console rows drew this grammar each for themselves, and two of them
 * sat in one file:
 *
 *   · `RecentPinnedPanel`'s recall row — `.rowMain`, `.rowText`, `.rowTitle`,
 *     `.rowContext`, `.rowWhen`, plus the `<li>` hover that painted it;
 *   · `RelationshipWorkspace`'s type rail — `.railItem`, `.railBody`,
 *     `.railLabel`, `.railMeta`;
 *   · and its candidate list — `.row`, `.rowBody`, `.rowPrimary`,
 *     `.rowSecondary`.
 *
 * The workspace had already folded its two text blocks into one rule and
 * written down why it stopped there: "`Lookup.module.css:55-57` is a third
 * copy of the same idea in the kit, near but not identical … folding into it
 * would move pixels". It was also carrying a real defect the copies hid from
 * each other: the rail painted its hover with `--surf-2` on a rail whose
 * ground IS `--surf-2`, so hovering a type drew nothing. A shared hover that
 * is a tint of ink rather than a named surface cannot vanish on any ground.
 *
 * ── what it owns, and what it deliberately does not ────────────────────────
 * It owns the BOX (a real `<button>`, the list-row height with the pointer
 * floor under it, one hover, one focus ring, one selected treatment, one
 * refused treatment) and the TEXT BLOCK (a label over a truncating detail).
 *
 * It does not own the list around it, the ARIA pattern the list uses, or a
 * second control on the row. A listbox option, a grid cell's button and a
 * plain list item are all this row — `role`, `aria-selected`,
 * `aria-describedby`, `tabIndex` and the key handlers are the host's and pass
 * straight through, with the ref. A row action (a Remove, a Preview) is a
 * SIBLING of this button and never inside it: a button inside a button is
 * invalid, and a hover-only control does not exist on a touch screen.
 *
 * ── the size axis ──────────────────────────────────────────────────────────
 * Two sizes because two exist: the drawer's recall list (`sm` — 12.5px label,
 * 4px inset, the small radius) and the relationship dialog's rail and
 * candidates (`md` — 14px label, 8px inset). Both keep the same height rule.
 */
export const ListRow = forwardRef<HTMLButtonElement, ListRowProps>(function ListRow(
  { leading, label, detail, trailing, selected = false, size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(s.row, s[size], className)}
      data-list-row=""
      data-selected={selected ? '' : undefined}
      {...rest}
    >
      {leading != null && <span className={s.leading} aria-hidden="true">{leading}</span>}
      <span className={s.text}>
        <span className={s.label}>{label}</span>
        {detail != null && detail !== false && detail !== '' && <span className={s.detail}>{detail}</span>}
      </span>
      {trailing != null && trailing !== false && <span className={s.trailing}>{trailing}</span>}
    </button>
  )
})
